import {
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityIdUpdateProps,
  type EntityOnSaveProps,
  type EntityValues,
} from "@firecms/core";
import {
  canAdminAccount,
  getAccountPath,
  type AccountCollectionId,
} from "../utils";
import type { AuthExtras } from "../types/auth";
import { buildRemoveUserFromAccountEntityAction } from "../actions/buildRemoveUserFromAccountEntityAction";

export type AccountUserRole = "manager" | "locked";

export type AccountUser = {
  uid: string;
  role: AccountUserRole;
};

export const buildAccountUsersPath = (id: AccountCollectionId) =>
  `${getAccountPath(id)}/accountUsers`;

export const buildAccountUsersCollection = (id: AccountCollectionId) =>
  buildCollection<AccountUser>({
    name: "Account access",
    singularName: "Account user",
    description:
      "Who can administer this client account (spaces, content, and events list). Grant access via invitations; revoke with the membership action.",
    id: "account_users",
    path: buildAccountUsersPath(id),
    icon: "Badge",
    group: "Admin",
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canManage = canAdminAccount(id, extra);
      return {
        read: canManage,
        edit: false,
        create: false,
        delete: false,
      };
    },
    entityActions: [buildRemoveUserFromAccountEntityAction(id.accountId)],
    properties: {
      uid: buildProperty({
        name: "User ID",
        dataType: "string",
        readOnly: true,
        description: "Firebase Auth uid.",
      }),
      role: buildProperty({
        name: "Role",
        dataType: "string",
        readOnly: true,
        enumValues: {
          manager: "Manager",
          locked: "Locked",
        },
      }),
    },
    callbacks: buildEntityCallbacks({
      onIdUpdate({ values }: EntityIdUpdateProps): string {
        return values?.uid || "Select a user";
      },
      onPreSave({
        values,
      }: EntityOnSaveProps): Promise<Partial<EntityValues<AccountUser>>> {
        return Promise.resolve(values ?? {});
      },
    }),
  });
